import * as Blockly from 'blockly/core';
import { useBlockFunctionStore } from '@/stores/useBlockFunctionStore';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';

export const emptyFlyout: Blockly.utils.toolbox.ToolboxInfo = {
    kind: 'categoryToolbox',
    contents: [],
};

export function patchFlyoutNoDelete(ws: Blockly.WorkspaceSvg) {
    const flyout = (ws as any).getFlyout?.();
    if (!flyout) return;
    if (!flyout._originalGetClientRect) {
        flyout._originalGetClientRect = flyout.getClientRect.bind(flyout);
    }
    flyout.getClientRect = () => null;
}

function mkWithShadows(
    type: string,
    shadows: { inputName: string; shadowType: string; fieldName: string; value: string | number }[],
): Element {
    const b = Blockly.utils.xml.createElement('block');
    b.setAttribute('type', type);
    for (const s of shadows) {
        const value = Blockly.utils.xml.createElement('value');
        value.setAttribute('name', s.inputName);
        const shadow = Blockly.utils.xml.createElement('shadow');
        shadow.setAttribute('type', s.shadowType);
        const field = Blockly.utils.xml.createElement('field');
        field.setAttribute('name', s.fieldName);
        field.textContent = String(s.value);
        shadow.appendChild(field);
        value.appendChild(shadow);
        b.appendChild(value);
    }
    return b;
}

function mk(type: string): Element {
    const b = Blockly.utils.xml.createElement('block');
    b.setAttribute('type', type);
    return b;
}

const CUSTOM_BLOCK_XML: Partial<Record<string, () => Element>> = {
    delay: () =>
        mkWithShadows('delay', [{ inputName: 'DURATION', shadowType: 'number', fieldName: 'NUM', value: 1000 }]),
};

interface VarModel {
    getId(): string;
    getName(): string;
    getType(): string;
}

function mkGetBlock(blockType: string, varName: string): Element {
    const b = Blockly.utils.xml.createElement('block');
    b.setAttribute('type', blockType);
    const field = Blockly.utils.xml.createElement('field');
    field.setAttribute('name', 'VAR_LABEL');
    field.textContent = varName;
    b.appendChild(field);
    return b;
}

function pushVarList(vars: VarModel[], blockType: string, isBeforeLabel: boolean, result: Element[]): void {
    const blocks = vars.map((v) => mkGetBlock(blockType, v.getName()));
    blocks.forEach((b, idx) => {
        if (isBeforeLabel && idx === blocks.length - 1) b.setAttribute('gap', '60');
        result.push(b);
    });
}

export function flyoutXmlFor(id: string, workspace?: Blockly.WorkspaceSvg): Element[] {
    const cat = CATEGORIES[id as keyof typeof CATEGORIES];
    const entries = (cat?.blocks ?? []).filter((e) => e.visible);
    const types = entries.map((e) => e.type);

    const allVars = workspace ? (workspace.getVariableMap().getAllVariables() as unknown as VarModel[]) : [];
    const floatVars = allVars.filter((v) => v.getType() === 'float');
    const stringVars = allVars.filter((v) => v.getType() === 'String');
    const boolVars = allVars.filter((v) => v.getType() === 'bool');

    const result: Element[] = [];

    for (let i = 0; i < types.length; i++) {
        const type = types[i];
        const isBeforeLabel = types[i + 1]?.startsWith('__label__') ?? false;

        if (type === '__varlist_float__') {
            pushVarList(floatVars, 'var_get_number', isBeforeLabel, result);
            continue;
        }

        if (type === '__varlist_string__') {
            pushVarList(stringVars, 'var_get_string', isBeforeLabel, result);
            continue;
        }

        if (type === '__varlist_bool__') {
            pushVarList(boolVars, 'var_get_bool', isBeforeLabel, result);
            continue;
        }

        if (type === '__funclist__') {
            const fns = useBlockFunctionStore.getState().functions;

            if (fns.length === 0) {
                const spacer = Blockly.utils.xml.createElement('block');
                spacer.setAttribute('type', 'spacer');
                spacer.setAttribute('gap', '0');
                result.push(spacer);
                continue;
            }

            for (const fn of fns) {
                const label = Blockly.utils.xml.createElement('button');
                label.setAttribute('text', fn.name);
                label.setAttribute('callbackKey', `__flyout_label__func__fn__${fn.id}`);
                result.push(label);

                const callBlock = Blockly.utils.xml.createElement('block');
                callBlock.setAttribute('type', 'func_call');
                const callMutation = Blockly.utils.xml.createElement('mutation');
                callMutation.setAttribute('fn_id', fn.id);
                callMutation.setAttribute('fn_name', fn.name);
                callMutation.setAttribute('params', JSON.stringify(fn.params));
                if (fn.returnType) callMutation.setAttribute('return_type', fn.returnType);
                callBlock.appendChild(callMutation);
                result.push(callBlock);

                const defBlock = Blockly.utils.xml.createElement('block');
                defBlock.setAttribute('type', 'func');
                const defMutation = Blockly.utils.xml.createElement('mutation');
                defMutation.setAttribute('fn_id', fn.id);
                defMutation.setAttribute('fn_name', fn.name);
                defMutation.setAttribute('params', JSON.stringify(fn.params));
                defBlock.appendChild(defMutation);

                if (fn.returnType) {
                    // func_returnをDOにネストした状態で出力
                    const retBlock = Blockly.utils.xml.createElement('block');
                    retBlock.setAttribute('type', 'func_return');
                    const retMutation = Blockly.utils.xml.createElement('mutation');
                    retMutation.setAttribute('fn_id', fn.id);
                    retMutation.setAttribute('fn_name', fn.name);
                    retMutation.setAttribute('return_type', fn.returnType);
                    retBlock.appendChild(retMutation);

                    const statement = Blockly.utils.xml.createElement('statement');
                    statement.setAttribute('name', 'DO');
                    statement.appendChild(retBlock);
                    defBlock.appendChild(statement);

                    defBlock.setAttribute('gap', '60');
                    result.push(defBlock);
                } else {
                    defBlock.setAttribute('gap', '60');
                    result.push(defBlock);
                }
            }
            continue;
        }

        if (type.startsWith('__button__')) {
            const parts = type.replace('__button__', '').split('__');
            const callbackKey = parts[0];
            const text = parts[1] ?? callbackKey;
            const btn = Blockly.utils.xml.createElement('button');
            btn.setAttribute('text', text);
            btn.setAttribute('callbackKey', callbackKey);
            result.push(btn);

            const spacer = Blockly.utils.xml.createElement('block');
            spacer.setAttribute('type', 'spacer');
            spacer.setAttribute('gap', '-30');
            result.push(spacer);

            continue;
        }

        if (type.startsWith('__label__')) {
            const text = type.replace('__label__', '');
            const btn = Blockly.utils.xml.createElement('button');
            btn.setAttribute('text', text);
            btn.setAttribute('callbackKey', `__flyout_label__${id}__${text}`);
            btn.setAttribute('width', '200');
            result.push(btn);
            continue;
        }

        const custom = CUSTOM_BLOCK_XML[type];
        const el = custom ? custom() : mk(type);
        if (isBeforeLabel) {
            el.setAttribute('gap', '60');
        }
        result.push(el);
    }

    return result;
}
